import type { FieldValue, SimpleFieldConfig } from "../types";
import { convertFieldValue } from "./field-helpers";

/**
 * Converts a raw cell value to a string for use in edit inputs (edit buffer)
 * Handles date, datetime, boolean, and other field types
 *
 * @param value - The raw field value (can be any FieldValue type)
 * @param fieldConfig - The field configuration
 * @returns A string suitable for form inputs
 */
export function valueToEditBuffer(value: FieldValue, fieldConfig: SimpleFieldConfig): string {
  if (value === null || value === undefined) {
    return "";
  }

  switch (fieldConfig.type) {
    case "date":
    case "due-date":
      // For DATE and DUE-DATE fields, use the string as-is (already in YYYY-MM-DD format)
      // This prevents timezone offset issues that occur when parsing through UTC
      if (typeof value === "string") {
        return value;
      }
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      return String(value);

    case "datetime":
      // Convert datetime to YYYY-MM-DDTHH:MM format for datetime-local input
      let date: Date;
      if (typeof value === "string") {
        date = new Date(value);
      } else if (value instanceof Date) {
        date = value;
      } else {
        date = new Date(String(value));
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;

    case "boolean":
      return String(value);

    default:
      return String(value);
  }
}

/**
 * Converts an edit buffer (string from form input) back to a FieldValue
 * Uses the centralized convertFieldValue logic to ensure consistency
 *
 * @param editValue - The string value from the edit input
 * @param fieldConfig - The field configuration
 * @returns The converted FieldValue
 */
export function editBufferToValue(editValue: string, fieldConfig: SimpleFieldConfig): FieldValue {
  return convertFieldValue(editValue, fieldConfig);
}
